import { hotelAccessCodeCrudConfig } from '@/config/objects/hotel-access-codes';
import {
  Button,
  CrudFormView,
  FormPageLayout,
  type CrudFormViewRef,
} from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export function HotelAccessCodeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formRef = useRef<CrudFormViewRef>(null);

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  // In edit mode, remove 'code' from form sections so it can't be changed
  const config = useMemo(() => {
    if (!isEdit) return hotelAccessCodeCrudConfig;
    return {
      ...hotelAccessCodeCrudConfig,
      form: {
        ...hotelAccessCodeCrudConfig.form,
        sections: hotelAccessCodeCrudConfig.form.sections?.map(section => ({
          ...section,
          fields: section.fields.filter(f => f !== 'code'),
        })),
      },
    };
  }, [isEdit]);

  const handleSuccess = (accessCode: { id: string }) => {
    if (isEdit) {
      navigate(-1);
    } else {
      navigate(`/hotel-access-codes/${accessCode.id}`);
    }
  };

  const handleCancel = () => {
    navigate(isEdit ? `/hotel-access-codes/${itemId}` : '/hotel-access-codes');
  };

  const handleSave = () => {
    formRef.current?.submit();
  };

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Access Code' : 'Create Access Code'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit ? itemId : 'Add a new hotel access code'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={formRef.current?.isPending}
            icon={Save}
          >
            {isEdit ? 'Save Changes' : 'Create Access Code'}
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <CrudFormView
          ref={formRef}
          config={config}
          itemId={itemId}
          onSuccess={handleSuccess}
          renderHeader={false}
          renderFooter={false}
        />
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={formRef.current?.lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={formRef.current?.isPending}
            icon={Save}
          >
            {isEdit ? 'Save Changes' : 'Create Access Code'}
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
